import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  beautyDeleteProfessionalApi,
  beautyListProfessionalsApi,
  beautyListServicesApi,
  beautyReorderProfessionalsApi,
} from "../../api/organizerBeautyApi";
import SpinLoading from "../../components/spinLoading/SpinLoading";
import { copiarAlPortapapeles } from "../../helpers/portapapeles";
import useCobrosHabilitados from "../hooks/useCobrosHabilitados";
import ProfesionalForm from "./ProfesionalForm";
import { MSG_ERROR_MODO, sincronizarModoReserva } from "./salonModo";
import { ConfirmModal, OrgToast } from "./TurnosUi";
import "./turnos-beauty.css";
import "../reservas/reservas-organizer.css";

/* ─────────────────────────────────────────
   ProfesionalesTab — equipo del salón (GET /professionals) con sus servicios, precios propios,
   horario semanal y acceso a ZOCO. Diseño inspirado en el prototipo Zoco Beauty (Artboard 6).
───────────────────────────────────────── */

const AVATAR_COLORS = ["zr-avatar--c1", "zr-avatar--c2", "zr-avatar--c3", "zr-avatar--c4", "zr-avatar--c5"];

// Link personal de reservas: el que arma el back (bookingUrl) o, si no vino, el de la ruta pública del front.
const linkDeReservas = (salon, p) =>
  p.bookingUrl || `${window.location.origin}/beauty/${salon?.slug || salon?._id}/${p.slug}`;

const cantidadServicios = (p) => (p.serviceIds || p.services || []).length;

const ProfesionalesTab = ({ salon, onIrAServicios, onSalonChange }) => {
  const [items, setItems] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [seleccion, setSeleccion] = useState(null); // id of the selected professional, "nuevo" for a new one, null = the first
  const [aBorrar, setABorrar] = useState(null);
  const [borrando, setBorrando] = useState(false);
  const [toast, setToast] = useState("");
  const esEmpleado = useSelector((s) => s.auth)?.user?.tipo === "empleado";
  const { cargando, puedeCobrar } = useCobrosHabilitados();
  const senasBloqueadas = cargando || !puedeCobrar;

  const cargar = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [pro, srv] = await Promise.all([beautyListProfessionalsApi(), beautyListServicesApi()]);
      const profesionales = Array.isArray(pro?.data?.info) ? pro.data.info : [];
      const catalogo = Array.isArray(srv?.data?.info) ? srv.data.info : [];
      setItems(profesionales);
      setServicios(catalogo);
      return { profesionales, servicios: catalogo };
    } catch (e) {
      setError(e?.response?.data?.message || "No pudimos cargar a tu equipo. Probá de nuevo.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const cerrarToast = useCallback(() => setToast(""), []);

  // Deposit overrides count for the salon's mode: after any change, re-derive it (never blocks the save).
  const sincronizarModo = async (cargado) => {
    if (!cargado) return;
    const r = await sincronizarModoReserva(salon, { puedeCobrar, cargando, ...cargado });
    if (r.patch && onSalonChange) onSalonChange(r.patch);
    if (r.error) setToast(MSG_ERROR_MODO);
  };

  const guardado = async (mensaje, id) => {
    setSeleccion(id || null);
    setToast(mensaje);
    await sincronizarModo(await cargar());
  };

  // Linking changes only `linked`; the form stays open, the list refreshes quietly behind it.
  const vinculoCambiado = async (mensaje) => {
    setToast(mensaje);
    try {
      const res = await beautyListProfessionalsApi();
      if (Array.isArray(res?.data?.info)) setItems(res.data.info);
    } catch (_) {}
  };

  const confirmarBorrado = async () => {
    setBorrando(true);
    try {
      await beautyDeleteProfessionalApi(aBorrar._id);
      setABorrar(null);
      setToast("Profesional eliminado");
      await sincronizarModo(await cargar());
    } catch (e) {
      setABorrar(null);
      setToast(e?.response?.data?.message || "No pudimos eliminar al profesional. Probá de nuevo.");
    } finally {
      setBorrando(false);
    }
  };

  const copiarLink = async (p) => {
    try {
      await copiarAlPortapapeles(linkDeReservas(salon, p));
      setToast("Link copiado al portapapeles.");
    } catch (_) {
      setToast("No se pudo copiar el link.");
    }
  };

  const mover = async (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= items.length) return;
    const previo = items;
    const nuevo = items.slice();
    [nuevo[i], nuevo[j]] = [nuevo[j], nuevo[i]];
    setItems(nuevo);
    try {
      await beautyReorderProfessionalsApi(nuevo.map((p) => p._id));
    } catch (e) {
      setItems(previo);
      setToast(e?.response?.data?.message || "No pudimos cambiar el orden. Probá de nuevo.");
    }
  };

  if (loading) return <SpinLoading />;

  if (error) {
    return (
      <div className="org-page-error">
        <p>{error}</p>
        <button type="button" onClick={cargar}>Reintentar</button>
      </div>
    );
  }

  const esNuevo = seleccion === "nuevo";
  const actual = esNuevo ? null : items.find((p) => p._id === seleccion) || items[0] || null;

  return (
    <div>
      {items.length === 0 && !esNuevo ? (
        <div className="zr-panel" style={{ padding: 0 }}>
          <div className="zr-empty" style={{ flexDirection: "column", gap: 12, textAlign: "center", padding: 24 }}>
            <p style={{ margin: 0 }}>Todavía no cargaste a nadie de tu equipo.</p>
            {servicios.length === 0 && (
              <>
                <p style={{ margin: 0 }}>Primero cargá tus servicios, así después elegís cuáles hace cada profesional.</p>
                {onIrAServicios && (
                  <button type="button" className="zr-btn zr-btn--ghost" onClick={onIrAServicios}>Ir a Servicios</button>
                )}
              </>
            )}
            <button type="button" className="zr-btn zr-btn--dark" onClick={() => setSeleccion("nuevo")}>
              Agregá tu primer profesional
            </button>
          </div>
        </div>
      ) : (
        <div className="zr-pro-split">
          <div className="zr-pro-list-card">
            <div className="zr-pro-list-header">
              <h2 className="zr-section-head__title" style={{ margin: 0 }}>Profesionales</h2>
            </div>
            <div className="zr-pro-list-scroll">
            {items.map((p, i) => {
              const colorClass = AVATAR_COLORS[i % AVATAR_COLORS.length];
              const inicial = (p.name || "P").charAt(0).toUpperCase();
              const seleccionado = !esNuevo && actual?._id === p._id;
              return (
                <div
                  key={p._id}
                  className={"zr-pro-item" + (seleccionado ? " zr-pro-item--selected" : "")}
                  onClick={() => setSeleccion(p._id)}
                >
                  <button
                    type="button"
                    className="zr-pro-item-main zr-pro-item-btn"
                    aria-label={`Editar ${p.name}`}
                    aria-pressed={seleccionado}
                    onClick={() => setSeleccion(p._id)}
                  >
                    <span className={`zr-avatar zr-avatar--md ${colorClass}`}>{inicial}</span>
                    <span className="zr-pro-item-text">
                      <span className="zr-pro-item-name">{p.name}</span>
                      {p.specialty?.trim() ? (
                        <span className="zr-pro-item-sub">{p.specialty.trim()}</span>
                      ) : null}
                    </span>
                  </button>
                  <span className={"zr-badge " + (p.active === false ? "zr-badge--pending" : "zr-badge--present")}>
                    <span className="dot" />{p.active === false ? "Inactivo" : "Activo"}
                  </span>
                </div>
              );
            })}
            </div>
            {esNuevo && (
              <div className="zr-pro-item zr-pro-item--selected">
                <span className="zr-pro-item-main">
                  <span className="zr-avatar zr-avatar--md">+</span>
                  <span className="zr-pro-item-text">
                    <span className="zr-pro-item-name">Nuevo profesional</span>
                  </span>
                </span>
              </div>
            )}
            <div className="zr-pro-list-footer">
              <button type="button" className="zr-btn zr-btn--dark" onClick={() => setSeleccion("nuevo")} disabled={esNuevo}>
                Agregar profesional
              </button>
            </div>
          </div>

          <ProfesionalForm
            key={actual?._id || "nuevo"}
            profesional={actual}
            servicios={servicios}
            salon={salon}
            senasBloqueadas={senasBloqueadas}
            onEliminar={actual ? () => setABorrar(actual) : null}
            onCopiarLink={actual && actual.slug && !esEmpleado ? () => copiarLink(actual) : null}
            onCancelar={() => setSeleccion(null)}
            onSaved={guardado}
            onVinculo={vinculoCambiado}
          />
        </div>
      )}

      {aBorrar && (
        <ConfirmModal title="¿Eliminar profesional?" busy={borrando} onConfirm={confirmarBorrado} onCancel={() => setABorrar(null)}>
          Se eliminará a <strong>{aBorrar.name}</strong>. Esta acción no se puede deshacer.
        </ConfirmModal>
      )}
      <OrgToast message={toast} onClose={cerrarToast} />
    </div>
  );
};

export default ProfesionalesTab;
